'use client';

import { formatPrice, quotedTotal } from '@/lib/price';
import {
  Box,
  ClipboardList,
  Layers,
  Pencil,
  Receipt,
  ShieldCheck,
  Smartphone,
  Tag,
  Wrench,
} from '@holistic/components/icons';

import type { StepParam } from './constants';
import type { SummaryRow, TalepRequestType } from './types';

import FlowCard from './flow-card';

const ROW_ICONS: Record<StepParam, typeof Box> = {
  category: Smartphone,
  brand: Tag,
  series: Layers,
  model: Box,
  type: Wrench,
};

type SummaryCardProps = {
  summary: SummaryRow[];
  /** Seçilen arızalar; ön teklif tutarı bunlardan hesaplanır. */
  requestTypes: TalepRequestType[];
  isWarrantyCovered: boolean;
  onEdit: (key: StepParam) => void;
};

const SummaryCard = ({
  summary,
  requestTypes,
  isWarrantyCovered,
  onEdit,
}: SummaryCardProps) => (
  <FlowCard icon={ClipboardList} title="Talep Özeti">
    <ul className="divide-y divide-gray-50">
      {summary.map((row) => {
        const Icon = ROW_ICONS[row.key];
        return (
          <li key={row.key}>
            <button
              type="button"
              onClick={() => onEdit(row.key)}
              className="group flex w-full cursor-pointer items-center gap-3 px-5 py-3 text-left transition-colors hover:bg-gray-50/70"
            >
              <span className="flex size-8 shrink-0 items-center justify-center rounded-lg bg-gray-50 text-gray-400">
                <Icon className="size-4" />
              </span>
              <span className="shrink-0 text-[13px] text-gray-400">
                {row.label}
              </span>
              {/* Arıza satırı birden fazla ad taşıyabildiği için sarmalı. */}
              <span className="line-clamp-2 flex-1 text-right text-sm leading-relaxed font-semibold break-words text-gray-900">
                {row.value}
              </span>
              <span className="text-primary flex shrink-0 items-center gap-1 text-[11px] font-medium opacity-60 transition-opacity group-hover:opacity-100">
                <Pencil className="size-3" />
                <span className="hidden sm:inline">Değiştir</span>
              </span>
            </button>
          </li>
        );
      })}
    </ul>

    {isWarrantyCovered ? (
      <div className="flex items-center gap-3 border-t border-emerald-100 bg-emerald-50/60 px-5 py-3">
        <span className="flex size-8 shrink-0 items-center justify-center rounded-lg bg-emerald-100 text-emerald-700">
          <ShieldCheck className="size-4" />
        </span>
        <span className="flex-1 text-[13px] text-emerald-800">
          Garanti Kapsamı
        </span>
        <span className="text-sm font-semibold text-emerald-700">Ücretsiz</span>
      </div>
    ) : (
      requestTypes.length > 0 && <EstimatedTotal requestTypes={requestTypes} />
    )}
  </FlowCard>
);

/**
 * Seçilen arızaların toplamı. Kalemlerden biri fiyatsızsa ya da analiz
 * gerektiriyorsa toplam yerine "inceleme sonrası" yazılır — backend de o
 * durumda `quotedPrice` üretmiyor.
 */
const EstimatedTotal = ({
  requestTypes,
}: {
  requestTypes: TalepRequestType[];
}) => {
  const total = quotedTotal(requestTypes);

  return (
    <div className="flex items-center gap-3 border-t border-gray-100 bg-gray-50/60 px-5 py-3">
      <span className="flex size-8 shrink-0 items-center justify-center rounded-lg bg-gray-100 text-gray-500">
        <Receipt className="size-4" />
      </span>
      <span className="flex-1 text-[13px] font-medium text-gray-600">
        Ön Teklif <span className="text-gray-400">(KDV dahil)</span>
      </span>
      <span className="text-sm font-bold text-gray-900">
        {total !== null ? formatPrice(total) : 'İnceleme sonrası'}
      </span>
    </div>
  );
};

export default SummaryCard;
